import { SERIE_FORMAT } from "@tokenterminal/core/feature/chart/ChartOptions"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { SelectField } from "@tokenterminal/core/ui/input/SelectField"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { type MetricConfiguration } from "hooks/trpc/useMetricsConfiguration"
import { useCallback, useMemo } from "react"
import {
  AutoWidthNumberInputPopoverRange,
  AutoWidthNumberInputPopoverSingle,
} from "./AutoWidthNumberInput"

import { operatorsWithLabels } from "./constants"
import { FilterTypeText, SelectedFilterBase } from "./SelectedFilterBase"

export type SelectedValueFilter = Omit<PresetColumnConfig, "value"> & {
  field: string
  display: string | undefined
  operator: string
  value: number | undefined
}

export type SelectedRangeFilter = Omit<PresetColumnConfig, "value"> & {
  field: string
  display: string | undefined
  operator: "between"
  value: (number | undefined)[] | undefined
}

export type SelectedFilter = SelectedValueFilter | SelectedRangeFilter

export const isRangeFilter = (
  filter: SelectedFilter
): filter is SelectedRangeFilter => {
  return filter.operator === "between"
}

const OperatorSelectField = ({
  filter,
  index,
  handleUpdateFilterByIndex,
  setAutoFocusFilterIndex,
}: {
  filter: SelectedFilter
  index: number
  handleUpdateFilterByIndex: ({
    index,
    newValue,
    debounced,
    persist,
  }: {
    index: number
    newValue: Partial<SelectedFilter>
    debounced?: boolean
    persist?: boolean
  }) => void
  setAutoFocusFilterIndex: (index: number) => void
}) => {
  const handleSelectionChange = useCallback(
    (newOperator: string) => {
      let shouldFocusInput = false
      let shouldPersist = true
      let updatedValue: number | (number | undefined)[] | undefined =
        filter.value

      // When moving from a "> value" filter to a "between" filter,
      // use the value as the lower bound and multiply it by 2 as the upper bound.
      if (
        !isRangeFilter(filter) &&
        filter.operator === ">" &&
        newOperator === "between"
      ) {
        const min = filter.value || 0
        const max = min * 2 || 100
        updatedValue = [min, max]
        shouldFocusInput = true

        // When moving from a "< value" filter to a "between" filter,
        // use the value as the upper bound and divide it by 2 as the lower bound.
      } else if (
        !isRangeFilter(filter) &&
        filter.operator === "<" &&
        newOperator === "between"
      ) {
        const max = filter.value || 100
        const min = max / 2 || 0
        updatedValue = [min, max]
        shouldFocusInput = true

        // When moving from a "between" filter to a "> value" filter
        // and if there is a lower bound set, use the lower bound as the value.
      } else if (
        isRangeFilter(filter) &&
        newOperator === ">" &&
        filter.value?.[0]
      ) {
        updatedValue = filter.value?.[0]

        // When moving from a "between" filter to a "> value" filter
        // and if there is no lower bound set, reset the filter and focus the input.
      } else if (
        isRangeFilter(filter) &&
        newOperator === ">" &&
        filter.value?.[0] === undefined
      ) {
        updatedValue = undefined
        shouldPersist = false
        shouldFocusInput = true

        // When moving from a "between" filter to a "< value" filter
        // and if there is no upper bound, reset the filter and focus the input.
      } else if (
        isRangeFilter(filter) &&
        newOperator === "<" &&
        filter.value?.[1]
      ) {
        updatedValue = filter.value?.[1]

        // When moving from a "between" filter to a "< value" filter
        // and if there is no upper bound, reset the filter and focus the input.
      } else if (
        isRangeFilter(filter) &&
        newOperator === "<" &&
        filter.value?.[1] === undefined
      ) {
        updatedValue = undefined
        shouldPersist = false
        shouldFocusInput = true

        // When changing the operator, if there is no value, focus the input.
      } else if (!isRangeFilter(filter) && filter.value === undefined) {
        updatedValue = undefined
        shouldFocusInput = true
        shouldPersist = false
      }

      if (newOperator === "between") {
        handleUpdateFilterByIndex({
          index,
          newValue: {
            operator: newOperator as "between",
            value: updatedValue as number[] | undefined,
          },
          persist: shouldPersist,
        })
      } else {
        handleUpdateFilterByIndex({
          index,
          newValue: {
            operator: newOperator,
            value: updatedValue as number | undefined,
          },
          persist: shouldPersist,
        })
      }
      if (shouldFocusInput) {
        setAutoFocusFilterIndex(index)
      }
    },
    [filter, handleUpdateFilterByIndex, index, setAutoFocusFilterIndex]
  )

  const operatorLabel = useMemo(() => {
    return operatorsWithLabels.find(
      (operator) => operator.value === filter.operator
    )?.label
  }, [filter.operator])

  return (
    <SelectField
      selectedKey={filter.operator}
      onSelectionChange={handleSelectionChange}
      aria-label="Filter operator"
      trigger={() => (
        <Button
          variant="ghost"
          className="!border-none !rounded-none !h-full !px-2 font-normal"
          size="small"
        >
          {operatorLabel}
        </Button>
      )}
    >
      {operatorsWithLabels.map((operator) => (
        <SelectField.Option key={operator.value} id={operator.value}>
          {operator.label}
        </SelectField.Option>
      ))}
    </SelectField>
  )
}

export const SelectedFilter = ({
  filter,
  index,
  handleRemoveFilterByIndex,
  handleUpdateFilterByIndex,
  autoFocusOnMount,
  clearAutoFocusOnMount,
  metricConfiguration,
  setAutoFocusFilterIndex,
}: {
  filter: SelectedFilter
  index: number
  handleRemoveFilterByIndex: (index: number) => void
  handleUpdateFilterByIndex: ({
    index,
    newValue,
    debounced,
    persist,
  }: {
    index: number
    newValue: Partial<SelectedFilter>
    debounced?: boolean
    persist?: boolean
  }) => void
  autoFocusOnMount: boolean
  clearAutoFocusOnMount: () => void
  metricConfiguration: MetricConfiguration | undefined
  setAutoFocusFilterIndex: (index: number) => void
}) => {
  // We use the metric's format unless the data type is a trend, in which case
  // we use percentage.
  const numberFormat = useMemo(() => {
    if (filter.cell_data_type === "trend") {
      return SERIE_FORMAT.PERCENTAGE
    }
    return metricConfiguration?.format
  }, [filter.cell_data_type, metricConfiguration])

  const transformValue = useCallback(
    (value: number | undefined): number | undefined => {
      if (value === undefined) {
        return undefined
      }

      if (numberFormat === SERIE_FORMAT.PERCENTAGE) {
        return value * 100
      }

      return value
    },
    [numberFormat]
  )

  const untransformValue = useCallback(
    (value: number | undefined): number | undefined => {
      if (value === undefined) {
        return undefined
      }

      if (numberFormat === SERIE_FORMAT.PERCENTAGE) {
        return value / 100
      }

      return value
    },
    [numberFormat]
  )

  const numberInputPrefix = useMemo(() => {
    if (numberFormat === SERIE_FORMAT.PRICE) {
      return "$"
    }
    return ""
  }, [numberFormat])

  const numberInputSuffix = useMemo(() => {
    if (numberFormat === SERIE_FORMAT.PERCENTAGE) {
      return "%"
    }
    return ""
  }, [numberFormat])

  return (
    <SelectedFilterBase
      filterTypeSlot={
        <FilterTypeText>
          <span className="text-fg-default">{filter.group}</span>{" "}
          <span className="text-fg-secondary">({filter.display})</span>
        </FilterTypeText>
      }
      operatorSlot={
        <div className="flex items-center h-full">
          <OperatorSelectField
            filter={filter}
            index={index}
            handleUpdateFilterByIndex={handleUpdateFilterByIndex}
            setAutoFocusFilterIndex={setAutoFocusFilterIndex}
          />
        </div>
      }
      valueSlot={
        <div data-filter-value className="h-full">
          {isRangeFilter(filter) ? (
            <AutoWidthNumberInputPopoverRange
              value={
                filter.value !== undefined
                  ? filter.value.map(transformValue)
                  : undefined
              }
              onChange={({ value: newValue, persist = true }) => {
                handleUpdateFilterByIndex({
                  index,
                  newValue: {
                    value:
                      newValue !== undefined
                        ? newValue.map(untransformValue)
                        : undefined,
                  },
                  debounced: true,
                  persist,
                })
              }}
              autoFocusOnMount={autoFocusOnMount}
              clearAutoFocusOnMount={clearAutoFocusOnMount}
              valueDisplayFormatter={(value) =>
                formatNumber({
                  value,
                  metricFormat: numberFormat,
                  format: "full",
                  // Only show the number of decimals that the value has
                  decimals: String(value).includes(".")
                    ? String(value).split(".")[1].length
                    : 0,
                })
              }
              numberInputPrefix={numberInputPrefix}
              numberInputSuffix={numberInputSuffix}
            />
          ) : (
            <AutoWidthNumberInputPopoverSingle
              value={
                filter.value !== undefined
                  ? transformValue(filter.value)
                  : undefined
              }
              onChange={({
                value: newValue,
                debounced = true,
                persist = true,
              }) => {
                handleUpdateFilterByIndex({
                  index,
                  newValue: {
                    value:
                      newValue !== undefined
                        ? untransformValue(newValue)
                        : undefined,
                  },
                  debounced,
                  persist,
                })
              }}
              autoFocusOnMount={autoFocusOnMount}
              clearAutoFocusOnMount={clearAutoFocusOnMount}
              valueDisplayFormatter={(value) =>
                formatNumber({
                  value,
                  metricFormat: numberFormat,
                  format: "full",
                  // Only show the number of decimals that the value has
                  decimals: String(value).includes(".")
                    ? String(value).split(".")[1].length
                    : 0,
                })
              }
              numberInputPrefix={numberInputPrefix}
              numberInputSuffix={numberInputSuffix}
            />
          )}
        </div>
      }
      handleRemoveFilter={() => handleRemoveFilterByIndex(index)}
    />
  )
}
